import React from "react";
import { YhModal } from "./YhModal";
import { YhButton } from "./YhButton";

export type ReminderPopupItem = {
  title?: string;
  body?: string;
  leadName?: string;
  uid?: string;
  leadId?: string;
  leadPhone?: string;
  reminderTitle?: string;
  reminderId?: string;
  due?: string;
};

type Props = {
  open: boolean;
  items: ReminderPopupItem[];
  heading?: string;
  onOk: () => void;
};

function reminderText(html?: string) {
  return String(html || "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+\n/g, "\n")
    .trim();
}

function stripClock(text?: string) {
  return reminderText(text).replace(/^⏰\s*/g, "").trim();
}

function lineValue(body: string, label: string) {
  const match = String(body || "").match(
    new RegExp(`^${label}:\\s*(.+)$`, "im")
  );
  const value = match ? match[1].trim() : "";
  return value && value !== "—" ? value : "";
}

function extractLeadId(text?: string) {
  const match = String(text || "").match(/\b(LD[-]?\d{2,})\b/i);
  return match ? match[1].toUpperCase().replace(/^LD-/, "LD") : "";
}

function extractLeadNameFromTitle(text?: string) {
  const match = String(text || "").match(
    /(?:reminder for|for)\s+(.+?)(?:\s+is\s+over|\s+is overdue|$)/i
  );
  return match ? match[1].trim() : "";
}

function DetailRow({
  label,
  value,
  always = false,
}: {
  label: string;
  value?: string;
  always?: boolean;
}) {
  const text = String(value || "").trim() || (always ? "—" : "");
  if (!text) return null;
  return (
    <div className="flex gap-2 text-sm leading-6">
      <span className="w-[88px] shrink-0 text-[#6B7280]">{label}</span>
      <span className="font-medium text-[#111827]">{text}</span>
    </div>
  );
}

export const ReminderPopup: React.FC<Props> = ({
  open,
  items,
  heading = "Reminder",
  onOk,
}) => {
  return (
    <YhModal
      open={open && items.length > 0}
      onClose={onOk}
      closable
      width={440}
      title={
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#F9F0F4] text-lg">
            ⏰
          </span>
          <span>{stripClock(heading) || "Reminder"}</span>
        </div>
      }
      footer={
        <div className="flex w-full justify-end">
          <YhButton type="button" variant="primary" size="sm" onClick={onOk}>
            OK
          </YhButton>
        </div>
      }
    >
      <ul className="space-y-3">
        {items.map((item, index) => {
          const title = stripClock(item.title);
          const headingClean = stripClock(heading);
          const showTitle =
            title &&
            title !== headingClean &&
            !/your reminder is overdue|reminder in 5 minutes/i.test(title);
          const body = reminderText(item.body);
          const leadName =
            reminderText(item.leadName) ||
            lineValue(body, "Lead") ||
            extractLeadNameFromTitle(`${title} ${body}`);
          const rawLeadId = reminderText(item.leadId);
          const uid =
            reminderText(item.uid) ||
            lineValue(body, "Lead ID") ||
            extractLeadId(
              `${item.uid} ${body} ${title} ${item.reminderTitle} ${rawLeadId}`
            ) ||
            (/^LD\d+/i.test(rawLeadId) ? rawLeadId : "");
          const phone =
            reminderText(item.leadPhone) || lineValue(body, "Mobile");
          const reminderTitle =
            reminderText(item.reminderTitle) || lineValue(body, "Reminder");
          const due = reminderText(item.due) || lineValue(body, "Due");
          return (
            <li
              key={`${item.reminderId || uid || title}-${index}`}
              className="rounded-xl border border-[#F3E8EC] bg-[#FCF8FA] px-4 py-3"
            >
              {showTitle ? (
                <p className="mb-2 text-sm font-semibold text-[#1E1B4B]">
                  {title}
                </p>
              ) : null}
              <div className="space-y-0.5">
                <DetailRow label="Lead" value={leadName} always />
                <DetailRow label="Lead ID" value={uid} always />
                <DetailRow label="Mobile" value={phone} always />
                <DetailRow label="Reminder" value={reminderTitle} />
                <DetailRow label="Due" value={due} />
              </div>
              {/overdue|5 minute/i.test(`${heading} ${title} ${body}`) ? (
                <p className="mt-2 text-sm leading-6 text-[#374151]">
                  {/overdue/i.test(`${heading} ${title} ${body}`)
                    ? "This follow-up is overdue. Please call this lead now."
                    : "Follow-up is in 5 minutes. Please call this lead now."}
                </p>
              ) : null}
            </li>
          );
        })}
      </ul>
    </YhModal>
  );
};

export default ReminderPopup;
